import { useLoader, useFrame } from '@react-three/fiber'
import { OBJLoader } from 'three/addons/loaders/OBJLoader'
import { useTexture, Text } from '@react-three/drei'
import { useMemo, useEffect, useRef } from 'react'
import * as THREE from 'three'

// TV screen geometry in group-local space (OBJ units × scale 0.05)
// Screen centre:  y = 42*0.05 − 1.5 = 0.6,  z = 27.5*0.05 = 1.375
// Screen size:    67*0.05 × 62*0.05  = 3.35 × 3.1 world units
const HALF_W = 1.67
const HALF_H = 1.31
const SCR_Y = 0.78
const SCR_Z = 1.375

// Fixed iframe resolution — content always renders at this size, CSS scale fits it to the overlay
const IFRAME_W = 960
const IFRAME_H = 752

export default function CrtTv({ url = 'https://adz.wtf/mac', position = [0, 0, 0] }) {
  const obj = useLoader(OBJLoader, '/models/crtTv_Final.obj')
  const overlayRef = useRef(null)
  const iframeRef = useRef(null)
  // Four preallocated Vector3s for the screen corners — avoids per-frame allocation
  const c = useMemo(() => Array.from({ length: 4 }, () => new THREE.Vector3()), [])

  const textures = useTexture({
    panasonicLogo: '/models/text/panasonicLogo_diffuse.jpg',
    tvFront: '/models/text/tvFront_diffuse.jpg',
    polishedSteel: '/models/text/polishedSteel_diffuse.jpg',
    tvGlass: '/models/text/tvGlass_Diffuse.jpg',
    powerCable: '/models/text/powerCable_diffuse.jpg',
    tvBack: '/models/text/tvBack_diffuse.jpg',
    tvBottom: '/models/text/tvBottom_diffuse.jpg',
    videoAudio: '/models/text/videoAudio_diffuse.jpg',
  })

  const tvObject = useMemo(() => {
    const clone = obj.clone(true)
    for (const child of [...clone.children]) {
      switch (child.name) {
        case 'panasonicLogo_Mesh':
          child.material = new THREE.MeshStandardMaterial({ map: textures.panasonicLogo })
          break
        case 'tvFront crtTv_grp':
          child.material = new THREE.MeshStandardMaterial({ map: textures.tvFront })
          break
        case 'crtTv_grp polishedMetal':
          child.material = new THREE.MeshStandardMaterial({ map: textures.polishedSteel })
          break
        case 'crtTv_grp tvGlass':
          child.material = new THREE.MeshStandardMaterial({
            map: textures.tvGlass,
            transparent: true,
            opacity: 0.15,
          })
          break
        case 'crtTv_grp powerCable':
          child.material = new THREE.MeshStandardMaterial({ map: textures.powerCable })
          break
        case 'crtTv_grp tvBack':
          child.material = new THREE.MeshStandardMaterial({ map: textures.tvBack })
          break
        case 'crtTv_grp tvBottom':
          child.material = new THREE.MeshStandardMaterial({ map: textures.tvBottom })
          break
        case 'crtTv_grp videoAudio_Mesh':
          child.material = new THREE.MeshStandardMaterial({ map: textures.videoAudio })
          break
        default:
          clone.remove(child)
      }
    }
    return clone
  }, [
    obj,
    textures.panasonicLogo,
    textures.tvFront,
    textures.polishedSteel,
    textures.tvGlass,
    textures.powerCable,
    textures.tvBack,
    textures.tvBottom,
    textures.videoAudio,
  ])

  // Create a fixed-position iframe overlay in the DOM
  useEffect(() => {
    const overlay = document.createElement('div')
    overlay.style.cssText =
      'position:fixed;overflow:hidden;pointer-events:none;z-index:1;display:none;'

    const iframe = document.createElement('iframe')
    iframe.src = url
    iframe.style.cssText = `width:${IFRAME_W}px;height:${IFRAME_H}px;border:none;background:#000;pointer-events:auto;transform-origin:top left;`
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups')

    overlay.appendChild(iframe)
    document.body.appendChild(overlay)
    overlayRef.current = overlay
    iframeRef.current = iframe

    return () => {
      document.body.removeChild(overlay)
    }
  }, [url])

  useFrame(({ camera, size }) => {
    const el = overlayRef.current
    if (!el) return

    const [px, py, pz] = position

    // World positions of the four screen corners (TL, TR, BR, BL)
    c[0].set(px - HALF_W, py + SCR_Y + HALF_H, pz + SCR_Z)
    c[1].set(px + HALF_W, py + SCR_Y + HALF_H, pz + SCR_Z)
    c[2].set(px + HALF_W, py + SCR_Y - HALF_H, pz + SCR_Z)
    c[3].set(px - HALF_W, py + SCR_Y - HALF_H, pz + SCR_Z)

    // Project into NDC, then into canvas CSS pixels
    // size.left / size.top account for a canvas that doesn't start at (0,0)
    const offL = size.left ?? 0
    const offT = size.top ?? 0
    const pts = c.map(v => {
      const n = v.clone().project(camera)
      return {
        x: ((n.x + 1) / 2) * size.width + offL,
        y: ((-n.y + 1) / 2) * size.height + offT,
        z: n.z,
      }
    })

    // If any corner is behind the near plane, hide
    if (pts.some(p => p.z > 1)) {
      el.style.display = 'none'
      return
    }

    const xs = pts.map(p => p.x)
    const ys = pts.map(p => p.y)
    const left = Math.min(...xs)
    const top = Math.min(...ys)
    const right = Math.max(...xs)
    const bottom = Math.max(...ys)
    const w = right - left
    const h = bottom - top

    // Hide when too small — prevents overlay bleeding over other webviews in the scene
    if (w < 150 || h < 150) {
      el.style.display = 'none'
      return
    }

    // Position the bounding box
    el.style.display = 'block'
    el.style.left = `${left}px`
    el.style.top = `${top}px`
    el.style.width = `${w}px`
    el.style.height = `${h}px`

    // Scale fixed-resolution iframe to fill the overlay — prevents re-layout as user approaches
    if (iframeRef.current) {
      iframeRef.current.style.transform = `scale(${w / IFRAME_W}, ${h / IFRAME_H})`
    }

    // Clip to a curved quadrilateral mimicking the CRT convex glass
    // Each edge bows inward using a quadratic bezier; control point = edge midpoint pushed toward centre
    const [tl, tr, br, bl] = pts.map(p => ({
      x: +(p.x - left).toFixed(1),
      y: +(p.y - top).toFixed(1),
    }))
    const cv = 10 // pixels of inward bow per edge
    const mx = (a, b) => +((a + b) / 2).toFixed(1)
    const path = [
      `M ${tl.x},${tl.y}`,
      `Q ${mx(tl.x, tr.x)},${mx(tl.y, tr.y) - cv} ${tr.x},${tr.y}`, // top edge bows up
      `Q ${mx(tr.x, br.x) + cv},${mx(tr.y, br.y)} ${br.x},${br.y}`, // right edge bows right
      `Q ${mx(br.x, bl.x)},${mx(br.y, bl.y) + cv} ${bl.x},${bl.y}`, // bottom edge bows down
      `Q ${mx(bl.x, tl.x) - cv},${mx(bl.y, tl.y)} ${tl.x},${tl.y}`, // left edge bows left
      'Z',
    ].join(' ')
    el.style.clipPath = `path('${path}')`
  })

  return (
    <group position={position}>
      <primitive object={tvObject} scale={0.05} position={[0, -1.5, 0]} />
      {/* Black screen — always visible; iframe overlays on top when close */}
      <mesh position={[0, SCR_Y, SCR_Z + 0.01]}>
        <planeGeometry args={[HALF_W * 2, HALF_H * 2]} />
        <meshBasicMaterial color='black' />
      </mesh>
      <Text
        position={[0, SCR_Y, SCR_Z + 0.02]}
        fontSize={0.18}
        color='#ffffff'
        anchorX='center'
        anchorY='middle'
        opacity={0.5}
      >
        come closer
      </Text>
    </group>
  )
}
